let activeAbortController: AbortController | null = null;

type ListUpdaterOptions = {
    allowPreHydration?: boolean;
};

type ListUpdaterItem = { itemNo?: string; itemId?: string };

const REQUEST_TIMEOUT_MS = 8000;
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 800;

export function ListUpdater(
    ref: HTMLOListElement,
    siteCode: string,
    languageCode: string,
    opts: ListUpdaterOptions = {}
) {
    const arrItems: ListUpdaterItem[] = [];
    const iteratorCount = 0;

    if (typeof window === 'undefined') return;

    if (!(window as any).__APP_HYDRATED__ && !opts.allowPreHydration) return;

    for (const itemElement of ref.querySelectorAll<HTMLElement>(".js-updateAlgoliaItemboxElement")) {
        arrItems.push({'itemNo': itemElement.dataset.itemno, 'itemId': itemElement.dataset.itemId});
    }
    if (arrItems.length === 0) return;

    // Cancel a still-pending previous request so a stale response can never overwrite newer results.
    activeAbortController?.abort();
    const controller = new AbortController();
    activeAbortController = controller;

    fetchWithRetry(arrItems, iteratorCount, siteCode, languageCode, controller.signal)
        .then((data) => handleResponse(data, arrItems, iteratorCount, siteCode, languageCode, controller.signal))
        .catch((error) => {
            if (isAbortError(error)) return;
            console.error('[ListUpdater] giving up after retries:', error);
            markAsFailed(arrItems);
        });
}

// The backend can respond 200 OK with valid JSON that simply has no entry for some
// requested items (e.g. a per-item pricing error on the server). That's not a network
// failure, so fetchWithRetry never sees it — handle it as its own retry path here.
async function handleResponse(
    data: any,
    requestedItems: ListUpdaterItem[],
    iteratorCount: number,
    siteCode: string,
    languageCode: string,
    signal: AbortSignal
) {
    const missing = applyUpdate(data, requestedItems);
    if (missing.length === 0) return;

    console.warn('[ListUpdater] backend response missing data for item(s), retrying once:', missing.map(i => i.itemId));
    try {
        const retryData = await fetchWithRetry(missing, iteratorCount, siteCode, languageCode, signal);
        const stillMissing = applyUpdate(retryData, missing);
        if (stillMissing.length > 0) {
            console.error('[ListUpdater] no data for item(s) after retry:', stillMissing.map(i => i.itemId));
            markAsFailed(stillMissing);
        }
    } catch (error) {
        if (isAbortError(error)) return;
        console.error('[ListUpdater] retry for missing item(s) failed:', error);
        markAsFailed(missing);
    }
}

function fetchOnce(
    arrItems: ListUpdaterItem[],
    iteratorCount: number,
    siteCode: string,
    languageCode: string,
    signal: AbortSignal
): Promise<any> {
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), REQUEST_TIMEOUT_MS);
    const onOuterAbort = () => timeoutController.abort();
    signal.addEventListener('abort', onOuterAbort);

    return fetch(`/module/dcshop/GeneralAjaxData.php?function=getAlgoliaItemListRender&site=${siteCode}&language=${languageCode}`, {
        method: "POST",
        mode: 'cors',
        cache: "no-cache",
        body: JSON.stringify({
            "item_list": arrItems,
            'counter': iteratorCount
        }),
        headers: {
            'Content-type': 'application/json; charset=UTF-8',
        },
        signal: timeoutController.signal,
    })
        .then(response => {
            if (!response.ok) throw new Error(`Unexpected response status ${response.status}`);
            return response.json();
        })
        .finally(() => {
            clearTimeout(timeoutId);
            signal.removeEventListener('abort', onOuterAbort);
        });
}

async function fetchWithRetry(
    arrItems: ListUpdaterItem[],
    iteratorCount: number,
    siteCode: string,
    languageCode: string,
    signal: AbortSignal
): Promise<any> {
    let lastError: unknown;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        try {
            return await fetchOnce(arrItems, iteratorCount, siteCode, languageCode, signal);
        } catch (error) {
            if (signal.aborted || isAbortError(error)) throw error;
            lastError = error;
            if (attempt < MAX_RETRIES) {
                await delay(RETRY_DELAY_MS * (attempt + 1));
            }
        }
    }
    throw lastError;
}

// Returns the subset of requestedItems that the response had no data for.
function applyUpdate(data: any, requestedItems: ListUpdaterItem[]): ListUpdaterItem[] {
    const missing: ListUpdaterItem[] = [];
    if (!data) return requestedItems;

    for (const item of requestedItems) {
        const item_id = item.itemId;
        if (!item_id || !(item_id in data)) {
            missing.push(item);
            continue;
        }

        const itemElement = document.getElementById("item_" + item_id);
        if (!itemElement) continue;

        const selectors = [
            {key: 'prices', selector: '.algoliaPrices'},
            {key: 'promotions', selector: '.algoliaPromotionsWrapper'},
            {key: 'orderbutton', selector: '.algoliaOrderbuttonWrapper'},
            {key: 'priceText', selector: '.algoliaPriceText'},
            {key: 'comparisonSign', selector: '.algoliaComparisonSign'},
            {key: 'favoriteSign', selector: '.algoliaFavoriteWrapper'},
            {key: 'variantButton', selector: '.algoliaVariantWrapper'},
            {key: 'inventorySign', selector: '.algoliaInventorySign'}
        ];

        selectors.forEach(({key, selector}) => {
            const element = itemElement.querySelector(selector);
            if (!element) return;
            element.innerHTML = data[item_id][key] ? data[item_id][key] : "";
        });

        itemElement.classList.remove("is-loaderror");
        itemElement.classList.add("is-loaded");
    }

    return missing;
}

// Stops the skeleton shimmer for items that never got a response, instead of leaving it stuck in "loading" forever.
function markAsFailed(arrItems: ListUpdaterItem[]) {
    for (const item of arrItems) {
        if (!item.itemId) continue;
        const itemElement = document.getElementById("item_" + item.itemId);
        if (!itemElement || itemElement.classList.contains("is-loaded")) continue;

        itemElement.classList.add("is-loaderror");
        itemElement.querySelectorAll<HTMLElement>(".loading, .loadSkeleton").forEach(el => {
            el.classList.remove("loading", "loadSkeleton");
        });
    }
}

function isAbortError(error: unknown): boolean {
    return error instanceof DOMException && error.name === 'AbortError';
}

function delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}
